import { Clock, Users, Award, Target, ArrowRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import executiveMeeting from "@/assets/executive-meeting.jpg";

const Programs = () => {
  const leadershipPrograms = [
    {
      title: "Strategic Leadership Excellence",
      duration: "30 days intensive / 12 weeks part-time",
      target: "Senior executives, C-suite leaders, department heads",
      outcomes: "Strategic thinking, change leadership, stakeholder management",
      certification: "Strategic Leadership Certificate",
      type: "Leadership",
      investment: "From $ 500 Individual",
    },
    {
      title: "Emerging Leaders Program",
      duration: "14 days intensive / 8 weeks part-time",
      target: "Middle management, team leaders, supervisors",
      outcomes: "Leadership transition, team building, performance management",
      certification: "Leadership Development Certificate",
      type: "Leadership",
      investment: "From $ 350 Individual",
    },
    {
      title: "Women in Leadership",
      duration: "21 days intensive / 10 weeks part-time",
      target: "Female professionals at all levels",
      outcomes: "Barrier overcome, confidence building, networking mastery",
      certification: "Women Leadership Excellence Certificate",
      type: "Leadership",
      investment: "From $ 250 Individual",
    },
  ];

  const specializedPrograms = [
    {
      title: "Digital Transformation Leadership",
      duration: "8 days intensive / 6 weeks part-time",
      target: "Digital leaders, IT managers, change agents",
      outcomes: "Digital innovation, technology adoption, change management",
      certification: "Digital Leadership Certificate",
      type: "Specialized",
      investment: "From $ 800 Individual",
    },
    {
      title: "Entrepreneurship & Business Development",
      duration: "14 days intensive / 10 weeks part-time",
      target: "Entrepreneurs, business developers, SME owners",
      outcomes: "Business planning, financial management, market strategy",
      certification: "Business Development Certificate",
      type: "Specialized",
      investment: "From $ 600 Individual",
    },
    {
      title: "Healthcare Leadership",
      duration: "14 days intensive / 8 weeks part-time",
      target: "Healthcare professionals, medical administrators",
      outcomes: "Patient-centered care, quality improvement, compliance",
      certification: "Healthcare Leadership Certificate",
      type: "Specialized",
      investment: "From $ 450 Individual",
    },
    {
      title: "Educational Leadership",
      duration: "12 days intensive / 8 weeks part-time",
      target: "Education administrators, school leaders",
      outcomes:
        "Academic administration, curriculum management, school culture",
      certification: "Educational Leadership Certificate",
      type: "Specialized",
      investment: "From $ 500 Individual",
    },
  ];

  const allPrograms = [...leadershipPrograms, ...specializedPrograms];

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Leadership":
        return "bg-primary text-primary-foreground py-1 px-10";
      case "Specialized":
        return "bg-secondary text-secondary-foreground py-1 px-10";
      default:
        return "bg-muted text-muted-foreground py-1 px-10";
    }
  };

  const cardBgColors = [
    "bg-blue-100",
    "bg-green-100",
    "bg-yellow-100",
    "bg-purple-100",
    "bg-pink-100",
    "bg-orange-100",
  ];

  return (
    <Layout>
    {/* Programs Overview */}
<section
  className="relative py-12 sm:py-8 md:py-24 lg:py-32 bg-gradient-subtle overflow-hidden px-4 sm:px-2 md:px-8 lg:px-14"
  id="programs"
>
  <div className="absolute inset-0 bg-blue-400/20"></div>
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
    style={{ backgroundImage: `url(${executiveMeeting})` }}
  ></div>
  <div className="container mx-auto px-2">
    <div className="text-center space-y-4 mb-12 sm:mb-8 md:mb-24">
      <h2 className="font-display font-bold text-2xl sm:text-2xl md:text-3xl lg:text-4xl mb-4">
        Our Comprehensive Training Portfolio
      </h2>
      <p className="text-sm sm:text-sm md:text-lg lg:text-xl text-black max-w-4xl w-full mx-auto">
        Choose from our extensive range of programs, available in both
        intensive and part-time formats to suit your schedule and learning
        preferences.
      </p>
    </div>
  </div>
</section>

      <section className="py-8 md:py-12 px-6">
        {/* Leadership Development Programs */}
        <div className="mb-16">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-blue-400 mb-8 border border-blue-200 px-6 py-3 rounded-md inline-block mx-auto text-center">
              Leadership Development Programs
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {leadershipPrograms.map((program, index) => (
              <Card
                key={index}
                className="h-full shadow-subtle hover:shadow-elegant transition-smooth w-full"
              >
                <CardHeader className="p-6">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <Badge className={getTypeColor(program.type)}>
                        {program.type}
                      </Badge>
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-foreground">
                      {program.title}
                    </h4>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-2 border-t border-border">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Clock className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Duration
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {program.duration}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Target Audience
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {program.target}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Target className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Expected Outcomes
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {program.outcomes}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Award className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Certification
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {program.certification}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-2 space-y-2 border-t border-border flex flex-col px-8">
                  {/* Investment & CTA */}
                  <div className="space-y-4">
                    {/* <div className="grid grid-cols-2 items-center justify-between w-full">
                      <span className="text-sm text-muted-foreground">
                        Investment:
                      </span>
                      <span className="text-right font-semibold text-yellow-500 justify-end">
                        {program.investment}
                      </span>
                    </div> */}

                    <div className="flex gap-3">
                      <Link to="/contact" className="flex-1">
                        <Button className=" bg-green-500 text-white hover:bg-blue-400 hover:text-white w-full group-hover:scale-105 transition-transform duration-300">
                          Enroll Now
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Button variant="outline" className="px-6">
                        Learn More
                      </Button>
                    </div>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>
      <section className="py-4 md:py-8 px-4">
        {/* Specialized Programs */}
        <div>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-blue-400 mb-8 border border-blue-200 px-6 py-3 rounded-md inline-block mx-auto text-center">
              Specialized Programs
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {specializedPrograms.map((program, index) => {
              const bg = cardBgColors[index % cardBgColors.length];

              return (
                <Card
                  key={index}
                  className={`h-full shadow-subtle hover:shadow-elegant transition-smooth ${bg}`}
                >
                  <CardContent className="p-6 space-y-6">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <Badge className={getTypeColor(program.type)}>
                          {program.type}
                        </Badge>
                        <Award className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="text-lg font-bold text-foreground">
                        {program.title}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Clock className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Duration
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {program.duration}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Target
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {program.target}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <Target className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Focus
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {program.outcomes}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="p-4 space-y-2 border-t border-border flex flex-col gap-4">
                    {/* Investment & CTA */}
                    <div className="space-y-4">
                      {/* <div className="grid grid-cols-2 items-center justify-between w-full">
                        <span className="text-sm text-muted-foreground">
                          Investment:
                        </span>
                        <span className="text-right font-semibold text-yellow-500">
                          {program.investment}
                        </span>
                      </div> */}

                      <div className="flex gap-3">
                        <Link to="/contact" className="flex-1">
                          <Button className=" bg-white text-blue-400 hover:bg-green-400 hover:text-white w-full group-hover:scale-105 transition-transform duration-300">
                            Enroll Now
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                        <Button variant="outline" className="px-6">
                          Learn More
                        </Button>
                      </div>
                    </div>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Program Features */}
<section className="py-16 lg:py-24 bg-gradient-secondary px-3">
  <div className="container mx-auto px-4">
    <div className="max-w-8xl mx-auto">
      <div className="text-center space-y-4 mb-12">
        <h2 className="text-2xl font-bold text-foreground">
          What Makes Our Programs Different?
        </h2>
        <p className="text-lg text-muted-foreground">
          Every program is designed with practical application and
          measurable outcomes in mind.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 w-full">
        <Card className="text-center shadow-subtle">
          <CardContent className="p-1 py-3 space-y-4">
            <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
              <Award className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-foreground">
              Certified Programs
            </h3>
            <p className="text-sm text-muted-foreground">
              All programs come with internationally recognized
              certificates
            </p>
          </CardContent>
        </Card>

        <Card className="text-center shadow-subtle">
          <CardContent className="p-1 py-3 space-y-4">
            <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-foreground">
              Expert Trainers
            </h3>
            <p className="text-sm text-muted-foreground">
              Learn from industry experts with proven track records
            </p>
          </CardContent>
        </Card>

        <Card className="text-center shadow-subtle">
          <CardContent className="p-1 py-3 space-y-4">
            <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-foreground">
              Practical Focus
            </h3>
            <p className="text-sm text-muted-foreground">
              Hands-on learning with immediate workplace application
            </p>
          </CardContent>
        </Card>

        <Card className="text-center shadow-subtle">
          <CardContent className="p-1 py-3 space-y-4">
            <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mx-auto">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold text-foreground">
              Flexible Delivery
            </h3>
            <p className="text-sm text-muted-foreground">
              Choose from intensive, part-time, or blended learning
              formats
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
</section>


      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-primary text-white px-4">
        <div className="container mx-auto px-4 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-2xl lg:text-3xl font-bold">
              Your Learning Journey Starts Here
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Explore our training programs and choose the path that will
              elevate your skills and transform your career.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" asChild>
              <Link to="/contact">
                Enroll Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="border-white text-black hover:bg-white hover:text-primary"
              asChild
            >
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Programs;
